// src/telemetry/ai.ts
import { ApplicationInsights, ITelemetryItem, type ITelemetryPlugin } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ENV } from "@/config/env";

const reactPlugin = new ReactPlugin();

export const appInsights = new ApplicationInsights({
  config: {
    connectionString: ENV.APPLICATIONINSIGHTS_CONNECTION_STRING,
    // Auto-collection ENABLED (safe because init only happens after consent is granted)
    enableAutoRouteTracking: false, // ❌ SPA route tracking disabled (handled manually for better duration metrics)
    disableAjaxTracking: false, // ✅ AJAX/dependency tracking enabled
    disableExceptionTracking: false, // ✅ Uncaught exception tracking enabled
    disableFetchTracking: false, // ✅ Fetch request tracking enabled
    enableAjaxErrorStatusText: true, // ✅ Error detail tracking
    enableAjaxPerfTracking: true, // ✅ Performance tracking
    samplingPercentage: 100,
    extensions: [reactPlugin as unknown as ITelemetryPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: undefined },
    },
  },
});

type TelemetryProperties = Record<string, unknown>;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let appInsightsLoaded = false;
let commonPropsRegistered = false;
let lastTrackedPath: string | null = null;
let navigationStart = typeof performance !== "undefined" ? performance.timeOrigin || Date.now() : Date.now();

if (typeof window !== "undefined" && window.history) {
  const originalPushState = window.history.pushState;
  window.history.pushState = function (...args: Parameters<typeof originalPushState>) {
    navigationStart = Date.now();
    return originalPushState.apply(this, args);
  };

  const originalReplaceState = window.history.replaceState;
  window.history.replaceState = function (...args: Parameters<typeof originalReplaceState>) {
    navigationStart = Date.now();
    return originalReplaceState.apply(this, args);
  };

  window.addEventListener("popstate", () => {
    navigationStart = Date.now();
  });
}

function telemetryFeatureEnabled(): boolean {
  return Boolean(ENV.APPLICATIONINSIGHTS_CONNECTION_STRING);
}

function isAppInsightsInitialized(): boolean {
  try {
    return !!(appInsights && appInsights.core && appInsights.core.isInitialized?.());
  } catch {
    return false;
  }
}

export function initAI(): boolean {
  if (!telemetryFeatureEnabled()) {
    return false;
  }

  if (appInsightsLoaded && isAppInsightsInitialized()) {
    return true;
  }

  try {
    appInsights.loadAppInsights();
    appInsightsLoaded = true;
    return true;
  } catch (error) {
    console.error("[Telemetry] Failed to initialize Application Insights", error);
    return false;
  }
}

export function setCommonProps(): void {
  if (!telemetryFeatureEnabled() || !isAppInsightsInitialized() || commonPropsRegistered) {
    return;
  }

  const environment = import.meta.env.VITE_DEPLOY_ENV ?? (import.meta.env.DEV ? "dev" : "prod");
  const serviceName = import.meta.env.VITE_SERVICE_NAME ?? "app";

  appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
    envelope.tags = envelope.tags || {};
    envelope.tags["ai.cloud.role"] = serviceName;
    envelope.tags["ai.device.id"] = environment;

    // Sanitize PageView names and Operation Names to remove UUIDs
    if (envelope.baseType === "PageviewData" && envelope.baseData) {
      const uri = envelope.baseData.uri || (typeof window !== "undefined" ? window.location.href : "");
      let path = "";
      try {
        path = new URL(uri).pathname;
      } catch {
        path = uri;
      }

      const { sanitizedPath, extractedIds } = sanitizePath(path);

      // Overwrite the name (which defaults to document.title in auto-tracking)
      envelope.baseData.name = sanitizedPath;

      // Set the operation name to group requests effectively
      envelope.tags["ai.operation.name"] = sanitizedPath;

      // Calculate duration (Time since last navigation start)
      const duration = Date.now() - navigationStart;
      envelope.baseData.duration = msToTimeSpan(duration);

      // Add extracted IDs to custom dimensions
      envelope.baseData.properties = {
        ...envelope.baseData.properties,
        ...extractedIds,
      };
    }
  });

  commonPropsRegistered = true;
}

function sanitizePath(rawPath: string): { sanitizedPath: string; extractedIds: Record<string, unknown> } {
  const parts = rawPath.split("/");
  const sanitizedParts: string[] = [];
  const extractedIds: Record<string, unknown> = {};
  const ids: string[] = [];

  for (const part of parts) {
    if (UUID_REGEX.test(part)) {
      sanitizedParts.push("{id}");
      ids.push(part);
    } else {
      sanitizedParts.push(part);
    }
  }

  if (ids.length > 0) {
    extractedIds.ids = ids;
  }

  return {
    sanitizedPath: sanitizedParts.join("/"),
    extractedIds,
  };
}

export function isTelemetryReady(): boolean {
  return appInsightsLoaded && isAppInsightsInitialized();
}

export function trackPageView(name: string, properties?: TelemetryProperties): void {
  if (!isTelemetryReady()) {
    return;
  }

  const hasWindow = typeof window !== "undefined";
  const rawPath = name || (hasWindow ? window.location.pathname : "");

  if (lastTrackedPath === rawPath) {
    return;
  }

  lastTrackedPath = rawPath;

  // Note: The TelemetryInitializer in setCommonProps will handle sanitization
  // and operation_name setting for both manual and auto-tracked views.
  appInsights.trackPageView({
    name: rawPath,
    uri: hasWindow ? window.location.href : undefined,
    properties,
  });
}

export function trackEvent(name: string, properties?: TelemetryProperties): void {
  if (!isTelemetryReady()) {
    return;
  }

  appInsights.trackEvent({ name, properties });
}

export function trackException(error: Error, properties?: TelemetryProperties): void {
  if (!isTelemetryReady()) {
    return;
  }

  appInsights.trackException({ exception: error, properties });
}

// Optional bridge: mirror important logs to AI as traces without removing console
export function note(message: string, properties: TelemetryProperties = {}): void {
  if (isTelemetryReady()) {
    appInsights.trackTrace({ message, properties });
  }

  // Keep console during transition period
  console.log(message, properties);
}

function msToTimeSpan(totalms: number): string {
  if (Number.isNaN(totalms) || totalms < 0) {
    totalms = 0;
  }
  const ms = String(totalms % 1000);
  const sec = String(Math.floor(totalms / 1000) % 60);
  const min = String(Math.floor(totalms / (1000 * 60)) % 60);
  const hour = String(Math.floor(totalms / (1000 * 60 * 60)) % 24);

  return `${hour}:${min}:${sec}.${ms}`;
}
